/**
 * Snippet tokens (decision 106) — placeholders inside cover-note/footer
 * snippets, expanded into REAL values from data already in the app at the
 * moment a snippet is INSERTED (Mark's call, 2026-08-07): the result is
 * ordinary editable text, nothing locks (#17's philosophy), and a token with
 * no data available stays visibly literal so a human spots and fills it.
 *
 * The token STRINGS live in the `snippet_tokens` table (managed on Admin →
 * Organisation) so new tokens are added in-app; the SOURCES they can map to
 * are the fixed vocabulary below — each needs code to supply its value.
 */

/** The in-app data sources a token may draw from. */
export const TOKEN_SOURCES = [
  { key: 'recipient_first_name', label: 'Recipient first name', description: 'The quote’s first recipient — their first name.' },
  { key: 'recipient_last_name', label: 'Recipient surname', description: 'The quote’s first recipient — their surname.' },
  { key: 'customer_name', label: 'Customer name', description: 'The end customer (school / trust) on the quote.' },
  { key: 'contract_end_date', label: 'Contract end date', description: 'Renewal quotes: the expiring contract’s end date (dd/mm/yyyy).' },
  { key: 'contract_arr', label: 'Contract ARR', description: 'Renewal quotes: the expiring contract’s annual recurring revenue (HubSpot hs_arr).' },
  { key: 'quote_number', label: 'Quote number', description: 'This quote’s number, e.g. W9R-2026-0001.' },
  { key: 'valid_until', label: 'Valid until', description: 'This quote’s validity date (dd/mm/yyyy).' },
  { key: 'owner_name', label: 'Quote owner', description: 'The Wave9 person who owns this quote.' },
  // Decision 148 (Mark's ask): the ISSUER as the customer sees it — a
  // reseller's own identity on their quotes, Wave9's otherwise. Website /
  // phone / email resolve at SEND on every quote (the server holds branding
  // and Organisation settings); at insert they may stay literal, like the
  // recipient tokens — the #128 re-expansion covers them.
  { key: 'partner_name', label: 'Partner name', description: 'The issuer’s name as the customer sees it — a reseller’s trading name on their quotes; Wave 9 Managed Services otherwise.' },
  { key: 'partner_website', label: 'Partner website', description: 'The issuer’s website — reseller branding, or the Wave9 Organisation settings.' },
  { key: 'partner_phone', label: 'Partner phone', description: 'The issuer’s contact phone — reseller branding, or the Wave9 Organisation settings.' },
  { key: 'partner_email', label: 'Partner contact email', description: 'The issuer’s contact email — a reseller’s reply-to address, or the Wave9 Organisation settings.' },
  { key: 'today_date', label: 'Today’s date', description: 'The date the token expands (dd/mm/yyyy) — at insert, or at send for tokens still literal.' },
] as const

export type TokenSourceKey = (typeof TOKEN_SOURCES)[number]['key']

export function isTokenSource(key: unknown): key is TokenSourceKey {
  return TOKEN_SOURCES.some((s) => s.key === key)
}

export interface SnippetToken {
  token: string
  source: string
}

/** The values available on THIS quote right now — absent keys leave tokens literal. */
export type TokenContext = Partial<Record<TokenSourceKey, string | null | undefined>>

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Expand tokens in a snippet body. Case-insensitive (#enddate works), longest
 * token first (#ARRUplift never loses its tail to #ARR), and a token must end
 * at a word boundary so #ARR leaves "#ARRx" alone. Unresolvable tokens stay
 * exactly as typed.
 */
export function expandSnippetTokens(
  text: string, tokens: SnippetToken[], ctx: TokenContext,
): string {
  let out = text
  const ordered = [...tokens].sort((a, b) => b.token.length - a.token.length)
  for (const t of ordered) {
    const value = isTokenSource(t.source) ? ctx[t.source] : undefined
    if (value == null || String(value).trim() === '') continue
    const re = new RegExp(`${escapeRegExp(t.token)}(?![A-Za-z0-9])`, 'gi')
    out = out.replace(re, String(value))
  }
  return out
}

/**
 * Decision 128: which of `tokens` still appear LITERALLY in the text.
 *
 * ⚠️ The UAT bug this exists for: #106 expands a snippet at INSERT time, and a
 * TEMPLATE stores the resulting text. On a fresh quote there is no recipient
 * yet, so `#FirstName` / `#LastName` cannot resolve, the literal token is what
 * gets saved into the template, and every quote made from that template
 * carried the literal into the customer's email — nothing ever re-expanded it.
 * Send now re-expands from the quote's real data, and this reports whatever is
 * STILL unresolved so the send panel can name it rather than mailing "#FirstName".
 */
export function unresolvedTokens(text: string | null | undefined, tokens: SnippetToken[]): string[] {
  if (!text) return []
  const found = new Set<string>()
  for (const t of tokens) {
    const re = new RegExp(`${escapeRegExp(t.token)}(?![A-Za-z0-9])`, 'i')
    if (re.test(text)) found.add(t.token)
  }
  return [...found].sort()
}
